import { useState, useRef } from "react";
import { Box, Typography, Popper, Paper, ClickAwayListener } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

type Option = {
  label: string;
  value: string;
};

type CustomSelectProps = {
  options: Option[];
  selected: string;
  onChange: (value: string) => void;
  variant?: "filled" | "text";
  disabled?: boolean;
};

const CustomEnableButton = ({ options, selected, onChange, variant = "filled", disabled= false }: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleSelect = (value: string) => {
    onChange(value);
    setIsOpen(false);
  };

  const selectedOption = options.find((opt) => opt.value === selected);
  const isEnabled = selected === "Enabled";

  // Shared color tokens
  const colors = {
    green: "#1EC96B",
    gray: "#A6A6B2",
  };

  // Styles differ by variant type
  const buttonStyles =
    variant === "filled"
      ? {
          backgroundColor: isOpen ? "transparent" : isEnabled ? colors.green : colors.gray,
          color: isOpen ? colors.gray : "#FFFFFF",
          border: isOpen ? "1px solid #A6A6B2": "1px solid transparent",
          paddingTop: "1px",
          paddingBottom: "1px",
          cursor: disabled ? "not-allowed" : "pointer",
          "&:hover": {
            backgroundColor: isOpen
              ? "transparent"
              : isEnabled
                ? "#158F4C"
                : "#575763",
          },
        }
      : {
          backgroundColor: "transparent",
          color: isOpen ? colors.gray : isEnabled ? colors.green : colors.gray,
          border: "1px solid transparent",
          paddingTop: "1px",
          paddingBottom: "1px",
          cursor: disabled ? "not-allowed" : "pointer",
          "&:hover": {
            color: isOpen || disabled
              ? colors.gray
              : isEnabled
                ? "#0D542D"
                : "#33333B",
          },
        };

  const displayText = isOpen ? "Select" : selectedOption?.label || "Select";

  return (
    <ClickAwayListener 
      onClickAway={() => {
        if (!disabled) {
          setIsOpen(false);
        }
      }}
    >
    <Box ref={wrapperRef} sx={{ position: "relative", display: "inline-flex" }}>
      <Box className="custom-enable-btn"
        ref={buttonRef}
        onClick={() => {
          if (!disabled) {
            setIsOpen((prev) => !prev);
          }
        }}
        sx={{
          ...buttonStyles,
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: "inherit",
          }}
        >
          {displayText}
        </Typography>

        <ArrowDropDownIcon
          sx={{
            fontSize: 18,
            color: "inherit",
          }}
        />
      </Box>
      <Popper
        open={isOpen}
        anchorEl={wrapperRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
          <Paper className="custom-dropdown-paper">
            {options.map((option, index) => {
              const isSelected = selected === option.value;
              return (
                <Box
                  className="custom-dropdown-option"
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  sx={{
                    borderBottom: index !== options.length - 1 ? "1px solid #f0f0f0" : "none",
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: "#333" }}>{option.label}</Typography>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid #6036D6",
                      backgroundColor: isSelected ? "#6036D6" : "transparent",
                    }}
                  />
                </Box>
              );
            })}
          </Paper>
      </Popper>
    </Box>
    </ClickAwayListener>
  );
};

export default CustomEnableButton;
